import {
  arrayToSentence,
  getRandomString,
  objectHasProperty,
  capitalizeFirstLetter,
} from 'utils/';
import {
  ITEM_PATH,
  ITEM_EDIT_PATH,
  ACCOUNT_SETUP_PATH,
} from 'constants/general';
import { history } from 'store/store';
import {
  StyledTable,
  StyledTableData,
  StyledTableHead,
  StyledTableBody,
  StyledTableFoot,
} from 'components/Table/Table/Table.styled';
import { FlexCol, FlexRow } from 'components/Layout/';
import { CheckBox, TextInput } from 'components/Inputs/';
import {
  StyledTableHeader,
  StyledTableHeadRow,
} from 'components/Table/Header/Header.styled';
import {
  StyledInput,
  IconWrapper,
  StyledWrapper,
  StyledCaption,
  StyledTableRow,
  StyledSubHeader,
  StyledRoundIcon,
  StyledRowButton,
  StyledSubHeading,
  StyledRoundButton,
  StyledInputButton,
  StyledTableWrapper,
  StyledInputWrapper,
  StyledErrorMessage,
  StyledButtonWrapper,
  StyledQuantityWrapper,
} from 'components/Item/Attribute/Attribute.styled';
import { LOST, GOOD, DAMAGED } from 'constants/inventory';
import { ReactComponent as TickIcon } from 'icons/tick.svg';
import { ReactComponent as EditIcon } from 'icons/edit_circle.svg';
import React, { useMemo, useState, Fragment, useEffect } from 'react';
import { CancelButton, RoundButton, DeleteButton } from 'components/Buttons/';

function Attribute({
  id,
  stock,
  statuses,
  onDelete,
  itemName,
  onSubmit,
  companyId,
  attributes,
  maxQuantity,
  onModalClose,
  isOnBoarding,
  isUpdatingQuantity,
}) {
  const entityType = 'inventory';
  const [quantity, setQuantity] = useState(stock);
  const error = { id: '', name: '', quantity: '' };
  const hasAttributes = attributes && attributes.length > 0;
  const row = { id: '', sku: '', name: '', total: 0, statuses };

  const items = useMemo(() => {
    if (!hasAttributes) {
      return [
        {
          ...row,
          id: getRandomString({ prefix: 'generated' }),
        },
      ];
    }
    const active = attributes.filter(function (a) {
      return a.quantity > 0;
    });
    const good = statuses.find(function (s) {
      return s.name === GOOD;
    });
    const grouped = active.reduce(function (acc, item) {
      let group = acc.find((g) => g.name === item.attribute_name);
      if (!group) {
        group = {
          total: 0,
          id: item.id,
          statuses: [],
          sku: item.sku,
          prevQuantity: 0,
          name: item.attribute_name,
        };
        acc.push(group);
      }
      let status = group.statuses.find(function (s) {
        return (
          s.id === item.condition || s.name === getStatusName(item.condition)
        );
      });
      if (!status) {
        const id = item.condition ? item.condition : good ? good.id : '';
        status = {
          id,
          quantity: 0,
          name: getStatusName(item.condition),
        };
        group.statuses.push(status);
      }
      group.total += item.quantity;
      status.quantity += item.quantity;
      group.prevQuantity += item.quantity;
      return acc;
    }, []);
    const requiredStatuses = [GOOD, LOST, DAMAGED];
    const statusIds = statuses.reduce(function (acc, status) {
      acc[status.name] = status.id;
      return acc;
    }, {});
    grouped.forEach(function (group) {
      requiredStatuses.forEach(function (statusName) {
        if (!group.statuses.find((status) => status.name === statusName)) {
          group.statuses.push({
            quantity: 0,
            name: statusName,
            id: statusIds[statusName],
          });
        }
      });
    });
    return grouped;
  }, [statuses, attributes]);

  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState(error);
  const [rows, setRows] = useState([...items]);
  const [isReadOnly, setIsReadOnly] = useState(hasAttributes);

  const checkedRows = useMemo(() => {
    return rows.filter(function (row) {
      return row.checked;
    });
  }, [rows]);

  function onEnableEditing() {
    setIsReadOnly(false);
  }

  function getStatusName(id) {
    const status = statuses.find(function (status) {
      return status.id === id;
    });
    return status ? status.name : GOOD;
  }

  /**
   * @param {{
   * currentTarget: {
   * name: string
   * value: string
   * }
   * }} event
   */
  function onInputChange(event) {
    const { currentTarget } = event;
    const { type, name, value, id: attrId } = currentTarget;
    const number = type === 'number';
    const conditionId = currentTarget.getAttribute('data-condition');
    const unsaved = isGeneratedId(attrId);
    const data = rows.map(function (row) {
      if (String(row.id) === attrId) {
        if (!conditionId) {
          return { ...row, [name]: value };
        } else {
          const result = number ? parseInt(value, 10) : value;
          const statuses = row.statuses.map(function (status) {
            if (status.id === conditionId) {
              return { ...status, [name]: result };
            }
            return status;
          });
          const total = statuses.reduce(function (sum, status) {
            return sum + status.quantity;
          }, 0);
          const sku = number && unsaved && result > 1 ? '' : row.sku;
          return { ...row, sku, total, statuses };
        }
      }
      return row;
    });
    setRows(data);
    isFormValid();
  }

  /**
   * @param {string} sign
   * @param {string} attributeId
   * @param {string} conditionId
   */
  function onQuantityChange(attributeId, conditionId, sign) {
    const unsaved = isGeneratedId(attributeId);
    const nextRows = rows.map(function (row) {
      if (row.id === attributeId) {
        const statuses = row.statuses.map(function (status) {
          if (status.id === conditionId) {
            const add = parseInt(status.quantity, 10) + 1;
            const minus = Math.max(parseInt(status.quantity, 10) - 1, 0);
            const quantity = sign === '+' ? add : minus;
            const sku = unsaved && quantity > 1 ? '' : status.sku;
            return { ...status, sku, quantity };
          }
          return status;
        });
        const total = statuses.reduce(function (sum, status) {
          return sum + status.quantity;
        }, 0);
        return { ...row, total, statuses };
      }
      return row;
    });
    setRows(nextRows);
    isFormValid();
  }

  /**
   * Validates the existing rows before adding a new one.
   */
  function isFormValid() {
    const errorObject = {};
    const row = rows[rows.length - 1];
    const { id, name, statuses } = row;
    if (!name) {
      errorObject.name = 'Enter name';
    } else {
      errorObject.name = '';
    }
    const total = getConditionTotal(statuses);
    if (total > 0) {
      errorObject.quantity = '';
    } else {
      errorObject.quantity = 'Enter quantity';
    }
    const hasError = Object.values(errorObject).some(function (value) {
      return value.length > 0;
    });
    if (hasError) {
      setErrors({ ...errorObject, id });
      return false;
    }
    setErrors(error);
    return true;
  }

  /**
   * @param {[]} row
   */
  function getConditionTotal(row) {
    const total = row.reduce(function (acc, { quantity }) {
      return acc + quantity;
    }, 0);
    return total;
  }

  function onAddAttribute() {
    if (isFormValid()) {
      setRows([
        ...rows,
        {
          ...row,
          id: getRandomString({ prefix: 'generated' }),
        },
      ]);
    }
  }

  function onCancel() {
    if (isUpdatingQuantity) {
      return onModalClose();
    }
    let path;
    if (isOnBoarding) {
      path = ACCOUNT_SETUP_PATH;
    } else {
      path = ITEM_PATH;
      if (id) {
        path = ITEM_EDIT_PATH.replace(':id', id);
      }
    }
    history.push(`${path}#general`);
  }

  /**
   * @param {boolean} updating
   */
  function processAttributeAction(updating) {
    const data = rows.map(function (row) {
      const { sku, name, statuses } = row;
      const props = {};
      if (!isGeneratedId(row.id)) {
        props.id = row.id;
      }
      if (!updating && sku) {
        props.sku = sku;
      }
      const conditions = statuses.reduce(function (acc, row) {
        acc[row.id] = row.quantity;
        return acc;
      }, {});
      return {
        ...props,
        conditions,
        entity_id: id,
        attribute_name: name,
        entity_type: entityType,
      };
    });

    const result = data.filter(function (row) {
      const saved = objectHasProperty(row, 'id');
      if (updating) {
        return saved;
      }
      return !saved;
    });
    return result.map(function (row) {
      const { id: _, ...rest } = row;
      return rest;
    });
  }

  /**
   *
   * @param {string} id
   */
  function onCheckChange(id) {
    const nextRows = rows.map(function (row) {
      const checked = !row.checked;
      if (id === 'all') {
        return { ...row, checked: checkedRows.length > 0 ? false : true };
      }
      if (row.id === id) {
        return { ...row, checked };
      }
      return row;
    });
    setRows(nextRows);
  }

  function onDeleteAttribute() {
    const saved = checkedRows.filter(function (row) {
      return !isGeneratedId(row.id);
    });
    if (saved.length > 0) {
      const attr = saved.map(function (row) {
        return {
          entity_id: id,
          entity_type: entityType,
          attribute_name: row.name,
        };
      });
      const attributes = { attributes: attr, company_id: companyId };
      onDelete(attributes);
      setMessage('');
    } else {
      setMessage('Select at least one saved attribute to delete');
    }
  }

  function onSaveAttribute() {
    let isValid = true;
    let errorObject = {};
    const messages = [];
    if (!id) {
      messages.push('Either save or edit an item before adding attributes');
      isValid = false;
    }
    if (rows.length === 1 && !rows[0].name) {
      messages.push('Add at least one attribute');
      isValid = false;
    }
    // find if any row does not have a name
    const hasEmptyName = rows.some(function (row) {
      errorObject = { id: row.id, name: row.name ? '' : 'Enter name' };
      return !row.name;
    });
    if (hasEmptyName) {
      isValid = false;
    }
    // find if any row does not have quantity
    const missingQuantity = rows.some(function (row) {
      const total = getConditionTotal(row.statuses);
      const hasQuantity = total > 0;
      errorObject = {
        ...errorObject,
        id: row.id,
        quantity: hasQuantity ? '' : 'Enter quantity',
      };
      return !hasQuantity;
    });
    setErrors(errorObject);
    if (missingQuantity) {
      isValid = false;
    }
    const available = rows.filter(function (row) {
      return row.name !== LOST;
    });
    const total = available.reduce(function (acc, row) {
      return acc + getConditionTotal(row.statuses);
    }, 0);
    if (total < quantity) {
      messages.push(
        `Good + Damaged ${itemName} must equal stock (${quantity})`,
      );
      isValid = false;
    }
    if (!isValid && messages.length > 0) {
      const message = arrayToSentence({ array: messages });
      setMessage(message);
      return;
    } else {
      setMessage('');
    }
    const addedAttributes = processAttributeAction(false);
    const updatedAttributes = processAttributeAction(true);
    if (isUpdatingQuantity) {
      const data = {
        id,
        quantity,
        creating_attributes: [],
        updating_attributes: updatedAttributes,
      };
      onSubmit(data);
    } else {
      const attributes = {
        company_id: companyId,
        creating_attributes: addedAttributes,
        updating_attributes: updatedAttributes,
      };
      onSubmit({ attributes });
    }
  }

  /**
   * @param {string | number} id
   */
  function isGeneratedId(id) {
    return typeof id === 'string' && id.includes('generated');
  }

  function onUpdateSKU(row) {
    const { sku, name } = row;
    const attrs = [
      {
        sku,
        id: row.id,
        entity_id: id,
        is_deleted: false,
        attribute_name: name,
        entity_type: entityType,
      },
    ];
    const attributes = {
      attributes: attrs,
      company_id: companyId,
    };
    onSubmit({ attributes, isUpdating: true });
  }

  function onStockChange(e) {
    const { value } = e.currentTarget;
    setQuantity(value ? Number(value) : '');
  }

  useEffect(() => {
    setQuantity(stock);
  }, [stock]);

  useEffect(() => {
    setRows(items);
  }, [items]);

  useEffect(() => {
    if (hasAttributes) {
      setIsReadOnly(hasAttributes);
    }
  }, [hasAttributes]);

  return (
    <StyledWrapper>
      {isUpdatingQuantity ? (
        <FlexRow>
          <FlexCol flexWidth={6}>
            <TextInput
              isRequired
              name="quantity"
              value={quantity}
              inputId="quantity"
              caption="Quantity"
              inputType="number"
              onChange={onStockChange}
              placeholder="Enter quantity"
              description={`How many pieces do you have (max ${maxQuantity})`}
            />
          </FlexCol>
        </FlexRow>
      ) : (
        <>
          <StyledCaption>Add Inventory Attributes</StyledCaption>
          <StyledSubHeading>This is optional</StyledSubHeading>
        </>
      )}
      <StyledTableWrapper $isUpdatingQuantity={isUpdatingQuantity}>
        <StyledTable>
          <StyledTableHead>
            <StyledTableHeadRow>
              {isUpdatingQuantity ? null : (
                <StyledTableHeader>
                  <CheckBox
                    isHeader
                    showCaption={false}
                    caption={'checkbox-header'}
                    inputTestId={'delete-header'}
                    inputId={'checkbox-id-header'}
                    inputName={'checkbox-name-header'}
                    onChange={onCheckChange.bind(null, 'all')}
                    isChecked={rows.length === checkedRows.length}
                  />
                </StyledTableHeader>
              )}
              <StyledTableHeader>
                Attribute Name
                <StyledSubHeader>e.g Color, Size, Shape e.t.c</StyledSubHeader>
              </StyledTableHeader>
              <StyledTableHeader>Condition</StyledTableHeader>
              <StyledTableHeader>Quantity</StyledTableHeader>
              {isUpdatingQuantity ? null : (
                <StyledTableHeader>Item code</StyledTableHeader>
              )}
            </StyledTableHeadRow>
          </StyledTableHead>
          <StyledTableBody>
            {rows.map(function (row) {
              const {
                sku,
                total,
                checked,
                statuses,
                id: attrId,
                prevQuantity,
                name: attrName,
              } = row;
              const unsaved = isGeneratedId(attrId);
              const hasError = errors.id === attrId;
              return (
                <Fragment key={attrId}>
                  {statuses.map(function (status, index) {
                    const {
                      prevSKU,
                      quantity,
                      id: statusId,
                      name: condName,
                    } = status;
                    const quantityError = hasError && condName === GOOD;
                    const name = `${attrName}-${condName}`;
                    return (
                      <StyledTableRow key={index}>
                        {index === 0 ? (
                          <>
                            {isUpdatingQuantity ? null : (
                              <StyledTableData
                                style={{ width: '5%' }}
                                rowSpan={statuses.length}
                              >
                                <CheckBox
                                  isChecked={checked}
                                  showCaption={false}
                                  caption={`checkbox-${attrName}`}
                                  inputTestId={`delete-${attrName}`}
                                  inputId={`checkbox-id-${attrName}`}
                                  inputName={`checkbox-name-${attrName}`}
                                  onChange={onCheckChange.bind(null, attrId)}
                                />
                              </StyledTableData>
                            )}
                            <StyledTableData
                              style={{ width: '20%' }}
                              rowSpan={statuses.length}
                            >
                              <StyledInput
                                id={attrId}
                                name="name"
                                data-condition=""
                                disabled={!unsaved}
                                value={attrName || ''}
                                onChange={onInputChange}
                                data-testid={`name_${name}`}
                                placeholder="Enter attribute name"
                              />
                              {hasError ? (
                                <StyledErrorMessage aria-live="polite">
                                  {errors.name}
                                </StyledErrorMessage>
                              ) : null}
                            </StyledTableData>
                          </>
                        ) : null}
                        <StyledTableData style={{ width: '20%' }}>
                          {capitalizeFirstLetter(condName)}
                        </StyledTableData>
                        <StyledTableData style={{ width: '35%' }}>
                          <StyledQuantityWrapper>
                            {isReadOnly && !isUpdatingQuantity ? null : (
                              <StyledRoundButton
                                $minus
                                onClick={onQuantityChange.bind(
                                  null,
                                  attrId,
                                  statusId,
                                  '-',
                                )}
                                data-testid={`minus-button-${name}`}
                              >
                                -
                              </StyledRoundButton>
                            )}
                            <StyledInput
                              id={attrId}
                              type="number"
                              name="quantity"
                              value={quantity || ''}
                              onChange={onInputChange}
                              data-condition={statusId}
                              data-testid={`quantity-${name}`}
                              placeholder={isReadOnly ? '0' : 'e.g 1'}
                              readOnly={isUpdatingQuantity ? false : isReadOnly}
                            />
                            {isReadOnly && !isUpdatingQuantity ? null : (
                              <StyledRoundButton
                                onClick={onQuantityChange.bind(
                                  null,
                                  attrId,
                                  statusId,
                                  '+',
                                )}
                                data-testid={`add-button-${name}`}
                              >
                                +
                              </StyledRoundButton>
                            )}
                          </StyledQuantityWrapper>
                          {quantityError ? (
                            <StyledErrorMessage aria-live="polite">
                              {errors.quantity}
                            </StyledErrorMessage>
                          ) : null}
                        </StyledTableData>
                        {index === 0 && !isUpdatingQuantity ? (
                          <StyledTableData rowSpan={statuses.length}>
                            <StyledInputWrapper $active={prevQuantity === 1}>
                              <StyledInput
                                id={attrId}
                                name="sku"
                                value={sku || ''}
                                data-condition=""
                                $unsaved={unsaved}
                                disabled={total > 1}
                                onChange={onInputChange}
                                placeholder="e.g 123456"
                                data-testid={`sku_${attrName}`}
                              />
                              <StyledInputButton
                                disabled={sku === prevSKU}
                                onClick={onUpdateSKU.bind(null, row)}
                                data-testid={`update-sku-${attrName}`}
                              >
                                <IconWrapper>
                                  <TickIcon />
                                </IconWrapper>
                              </StyledInputButton>
                            </StyledInputWrapper>
                          </StyledTableData>
                        ) : null}
                      </StyledTableRow>
                    );
                  })}
                </Fragment>
              );
            })}
          </StyledTableBody>
          {isUpdatingQuantity ? null : (
            <StyledTableFoot>
              <StyledTableRow>
                <StyledTableData colSpan={2}>
                  {isReadOnly ? null : (
                    <StyledRowButton onClick={onAddAttribute}>
                      <StyledRoundIcon>+</StyledRoundIcon>
                      Add Attribute
                    </StyledRowButton>
                  )}
                </StyledTableData>
                <StyledTableData></StyledTableData>
                <StyledTableData></StyledTableData>
                <StyledTableData></StyledTableData>
              </StyledTableRow>
            </StyledTableFoot>
          )}
        </StyledTable>
      </StyledTableWrapper>
      {message ? (
        <StyledErrorMessage $main aria-live="polite">
          {message}
        </StyledErrorMessage>
      ) : null}
      <FlexRow>
        <StyledButtonWrapper>
          {isReadOnly && !isUpdatingQuantity ? (
            <RoundButton
              caption="Edit"
              flexWidth={100}
              icon={<EditIcon />}
              onClick={onEnableEditing}
            />
          ) : (
            <RoundButton
              flexWidth={100}
              icon={<TickIcon />}
              onClick={onSaveAttribute}
              caption={hasAttributes ? 'Update' : 'Save'}
            />
          )}
        </StyledButtonWrapper>
        <StyledButtonWrapper>
          <CancelButton onClick={onCancel} />
        </StyledButtonWrapper>
        {checkedRows?.length > 0 ? (
          <StyledButtonWrapper>
            <DeleteButton onClick={onDeleteAttribute} />
          </StyledButtonWrapper>
        ) : null}
      </FlexRow>
    </StyledWrapper>
  );
}

export default Attribute;
